import type { FilteredProduct, PricingSchema } from '@/api';
import { isStockDecoration } from '@/api';

import type { ProductCardProps } from '@/shared-ui/ProductCard/ProductCard.types';
import { PRICE_TIER_ORDER, PRICE_TIER_RANGES } from './MedicationPadItemEditor.constants';
import type { PriceTier } from './MedicationPadItemEditor.types';

/**
 * True when the product cannot be prescribed as-is (out of stock, discontinued, inactive or unavailable).
 * A `StockDecoration` alone is only a shape, so the individual flags are checked.
 */
export const isOutOfServiceProduct = ({ decoration }: FilteredProduct): boolean =>
  isStockDecoration(decoration) &&
  (decoration.isOutOfStock || decoration.isDiscontinued || decoration.isInactive || decoration.isUnavailable);

export const getPriceTiers = (product: FilteredProduct): PriceTier[] =>
  PRICE_TIER_ORDER.filter((tier) => isInPriceTier(product.prices, tier));

export const isInPriceTier = (prices: PricingSchema | undefined, tier: PriceTier): boolean => {
  if (!prices) {
    return false;
  }

  const { discounted, normal } = prices;

  if (!discounted && !normal) {
    return false;
  }

  const { min, max } = PRICE_TIER_RANGES[tier];
  const price = prices.discounted ?? prices.normal ?? 0;

  return Number(price) >= min && Number(price) < max;
};

export const mapPricingSchemaToProductCard = (
  product: FilteredProduct,
  selectedPriceTier?: PriceTier | null,
): FilteredProduct & ProductCardProps => {
  const allPriceTierLabels = getPriceTiers(product);
  // Design feedback: if a pricing tier is selected then only show that tier on the product
  const priceTierLabels = selectedPriceTier
    ? allPriceTierLabels.filter((tier) => tier === selectedPriceTier)
    : allPriceTierLabels;

  return {
    ...product,
    price: product.prices?.normal,
    discountPrice: product.prices?.discounted,
    priceTierLabels,
  };
};
